import ExcelResultTable from "./ExcelResultTable";
import CalculationWarning from "./CalculationWarning";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Skeleton,
} from "@mui/material";

const CylindricalShellCalculation = ({ data, loading, error }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [activeView, setActiveView] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);

  const stiffeningRings = data?.tankCylindricalShellSheetResult?.cylindricalShellSection1?.stiffeningRings;
  const isTower = activeView === 1;


  useEffect(() => {
    setDetailLoading(true);
    const timer = setTimeout(() => setDetailLoading(false), 400);
    return () => clearTimeout(timer);
  }, [activeView]);

  const renderSkeleton = () => (
    <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
      <Box sx={{ flex: 1 }}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} /></Box>
      <Box sx={{ flex: 1 }}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} /></Box>
    </Box>
  );


  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Tabs
        value={activeView}
        onChange={(_, v) => setActiveView(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          mb: 3,
          "& .MuiTabs-flexContainer": { justifyContent: "center", gap: 1 },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.9rem",
            borderRadius: "8px 8px 0 0",
            color: "var(--text-secondary)",
            "&.Mui-selected": { color: "primary.main" },
          },
          "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 3 },
        }}
      >
        <Tab label={t("budgetForm.vesselCalc.tank")} />
        <Tab label={t("budgetForm.vesselCalc.tower")} />
      </Tabs>

      {error ? (
        <CalculationWarning error={error} />
      ) : data != null ? (
        <Box sx={{ width: "100%" }}>
          {detailLoading || loading ? renderSkeleton() : (
            <>
              <Box sx={{ display: "flex", gap: 3, mb: 2, flexDirection: { xs: "column", md: "row" } }}>
                <Box sx={{ flex: 1 }}>
                  <Paper sx={{ p: 2, minHeight: 400, height: "100%" }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} textAlign="center" sx={{ color: "var(--text-primary)" }}>
                      {t("budgetForm.cylindricalShellCalc.bodyTitle")} - [{isTower ? t("budgetForm.vesselCalc.tower") : t("budgetForm.vesselCalc.tank")}]
                    </Typography>
                    <ExcelResultTable tableType="CYLINDRICAL_SHELL_1" isTower={isTower} data={data} />
                  </Paper>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Paper sx={{ p: 2, minHeight: 400, height: "100%" }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} textAlign="center" sx={{ color: "var(--text-primary)" }}>
                      {t("budgetForm.cylindricalShellCalc.largestNozzleTitle")} - [{isTower ? t("budgetForm.vesselCalc.tower") : t("budgetForm.vesselCalc.tank")}]
                    </Typography>
                    <ExcelResultTable tableType="CYLINDRICAL_SHELL_2" isTower={isTower} data={data} />
                  </Paper>
                </Box>
              </Box>

              <Box>
                <Paper sx={{ p: 2, minHeight: 400, marginTop: 4 }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={2} textAlign="center" sx={{ color: "var(--text-primary)" }}>
                    {t("budgetForm.cylindricalShellCalc.openingCheckTitle")} - [{isTower ? t("budgetForm.vesselCalc.tower") : t("budgetForm.vesselCalc.tank")}]
                  </Typography>
                  <ExcelResultTable tableType="CYLINDRICAL_SHELL_3" isTower={isTower} data={data} />
                </Paper>
              </Box>

              {!isTower && stiffeningRings && (
                <Box sx={{ mt: 4 }}>
                  <Paper sx={{ p: 2, minHeight: 150, textAlign: "center" }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ textDecoration: "underline", mb: 3, color: "var(--text-primary)" }}>
                      {t("budgetForm.cylindricalShellCalc.stiffeningRingsTitle")}
                    </Typography>
                    <Box sx={{ textAlign: "left" }}>
                      {(() => {
                        const { diameterInMM, lmax, bodyLength, numberOfStiffeningRings } = stiffeningRings;
                        const safeDiameter = typeof diameterInMM === "number" ? diameterInMM.toFixed(2) : "-";
                        const safeLmax = lmax ?? "-";
                        const safeBodyLength = bodyLength ?? "-";
                        const safeNumberOfRings = numberOfStiffeningRings ?? "-";
                        return (
                          <>
                            <Typography variant="body2" mb={1} sx={{ color: "var(--text-primary)" }}>
                              {t("budgetForm.cylindricalShellCalc.stiffeningRingsLine1", { diameter: safeDiameter, lmax: safeLmax })}
                            </Typography>
                            <Typography variant="body2" mb={1} sx={{ color: "var(--text-primary)" }}>
                              {t("budgetForm.cylindricalShellCalc.stiffeningRingsLine2", { bodyLength: safeBodyLength, rings: safeNumberOfRings })}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: "var(--text-primary)" }}>
                              {t("budgetForm.cylindricalShellCalc.stiffeningRingsLine3", { rings: safeNumberOfRings })}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <CalculationWarning />
      )}

      <Backdrop
        open={loading}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.85)",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "primary.main" }} size={40} />
      </Backdrop>
    </Box>
  );
};

export default CylindricalShellCalculation;
